import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Alert, Button, Col, Nav, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { useCrewMember } from '../../entities/crewMember';
import { COLOR_PRIMARY, COLOR_SUCCESS, COLOR_SECONDARY, COLOR_BLUE, COLOR_GREEN } from '../../shared/colorUtils';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import StatusBadge from '../../components/ui/StatusBadge';
import StatsCard from '../../components/ui/StatsCard';
import EmptyState from '../../components/feedback/EmptyState';

const CrewDetail = () => {
  const { crewId } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('overview');

  const { data: crewMember, isLoading: loading, isError } = useCrewMember(crewId);
  const projects = crewMember?.active_projects ?? [];
  const statistics = crewMember?.project_statistics ?? null;
  const error = isError ? t('crew.errors.failedToLoadData') : null;

  const { data: allProjects = [], refetch: refetchAllProjects } = useQuery({
    queryKey: ['crew-all-projects', crewId],
    queryFn: async () => {
      const response = await api.get(`projects/api/projects/?crew_member=${crewId}`);
      return response.data || response || [];
    },
    enabled: activeTab === 'projects',
  });

  useRealtimeEvents((e) => {
    if (e.model === 'projects.project' || e.model === 'projects.projectstaff') {
      queryClient.invalidateQueries({ queryKey: ['crew-all-projects', crewId] });
    }
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('cs-CZ');
  };

  const getStatusText = (status) => {
    // Handle both string status and object status from API
    if (typeof status === 'object' && status?.name) {
      return status.name;
    }

    switch (status) {
      case 'active': return t('projects.status.active');
      case 'planning': return t('projects.status.planning');
      case 'completed': return t('projects.status.completed');
      case 'cancelled': return t('projects.status.cancelled');
      case 'on_hold': return t('projects.status.onHold');
      default: return status || '';
    }
  };

  const getStatusBadgeColor = (status) => {
    // Handle both string status and object status from API
    const statusName = typeof status === 'object' && status?.name ? status.name.toLowerCase() : status;

    switch (statusName) {
      case 'active':
      case 'aktivní':
      case 'plánováno':
        return COLOR_PRIMARY;
      case 'planning':
        return '#4299e1';
      case 'completed':
      case 'dokončeno':
        return COLOR_SUCCESS;
      case 'cancelled':
      case 'zrušeno':
        return '#d63939';
      case 'on_hold':
      case 'pozastaveno':
        return '#f59f00';
      default: return COLOR_SECONDARY;
    }
  };

  if (loading) {
    return (
      <PageShell pretitle={t('crew.title')} title={t('crew.detail', 'Detail člena týmu')}>
        <PageSection>
          <CenteredLoader message={t('crew.loading')} />
        </PageSection>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell pretitle={t('crew.title')} title={t('crew.detail', 'Detail člena týmu')}>
        <PageSection>
          <Alert variant="danger" className="mb-0">{error}</Alert>
        </PageSection>
      </PageShell>
    );
  }

  if (!crewMember) {
    return (
      <PageShell pretitle={t('crew.title')} title={t('crew.detail', 'Detail člena týmu')}>
        <PageSection>
          <EmptyState
            title={t('crew.notFound')}
            action={<Button as={Link} to="/crew">{t('common.back')}</Button>}
          />
        </PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('crew.title')}
      title={crewMember.display_name || `${crewMember.first_name} ${crewMember.last_name}`}
      description={(
        <>
          <StatusBadge className="me-2" color={COLOR_BLUE} label={crewMember.profession?.name || '-'} />
          {crewMember.company && (
            <StatusBadge color={COLOR_GREEN} label={crewMember.company.company_name || '-'} />
          )}
        </>
      )}
      actions={(
        <>
          <Button as={Link} to="/crew" variant="outline-secondary">
            <i className="ti ti-arrow-left me-2" aria-hidden="true" />
            {t('common.back')}
          </Button>
          <Button as={Link} to={`/crew/${crewId}/edit`}>
            <i className="ti ti-edit me-2" aria-hidden="true" />
            {t('common.edit')}
          </Button>
        </>
      )}
    >
      <div className="app-stat-grid mb-4">
        <StatsCard title={t('crew.totalProjects')} value={statistics?.total_projects || 0} icon="briefcase" colorClass="primary" />
        <StatsCard title={t('crew.activeProjects')} value={statistics?.active_projects || 0} icon="bolt" colorClass="success" />
        <StatsCard title={t('crew.form.defaultHourlyRate')} value={`${parseFloat(crewMember.default_hourly_rate || 0).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${t('crew.form.perHour')}`} icon="clock-dollar" colorClass="azure" />
        <StatsCard title={t('crew.form.defaultDailyRate')} value={`${parseFloat(crewMember.default_daily_rate || 0).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${t('crew.form.perDay')}`} icon="calendar-dollar" colorClass="orange" />
      </div>

      <PageSection bodyClassName="pt-3">
        <Nav className="app-tabnav" activeKey={activeTab}>
          <Nav.Item>
            <Nav.Link as="button" eventKey="overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
              <i className="ti ti-user me-2" aria-hidden="true" />
              {t('crew.overview')}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as="button" eventKey="projects" active={activeTab === 'projects'} onClick={() => setActiveTab('projects')}>
              <i className="ti ti-briefcase me-2" aria-hidden="true" />
              {t('projects.title')}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as="button" eventKey="statistics" active={activeTab === 'statistics'} onClick={() => setActiveTab('statistics')}>
              <i className="ti ti-chart-bar me-2" aria-hidden="true" />
              {t('crew.statistics')}
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <div className="pt-4">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <Row className="g-4">
                  <Col md={6}>
                    <PageSection
                      title={t('crew.personalInformation')}
                      description={t('crew.detailPage.personalInfoDescription')}
                    >
                        <div className="datagrid">
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('crew.form.firstName')}</div>
                            <div className="datagrid-content">{crewMember.display_first_name || crewMember.first_name}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('crew.form.lastName')}</div>
                            <div className="datagrid-content">{crewMember.display_last_name || crewMember.last_name}</div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('crew.form.email')}</div>
                            <div className="datagrid-content">
                              {crewMember.display_email || crewMember.email ? (
                                <a href={`mailto:${crewMember.display_email || crewMember.email}`}>
                                  {crewMember.display_email || crewMember.email}
                                </a>
                              ) : '-'}
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('crew.form.mobile', 'Mobil')}</div>
                            <div className="datagrid-content">
                              {crewMember.company?.mobile ? <a href={`tel:${crewMember.company.mobile}`}>{crewMember.company.mobile}</a> : '-'}
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('crew.form.telephone', 'Telefon')}</div>
                            <div className="datagrid-content">
                              {crewMember.company?.telephone ? <a href={`tel:${crewMember.company.telephone}`}>{crewMember.company.telephone}</a> : '-'}
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('crew.form.profession')}</div>
                            <div className="datagrid-content">
                              <StatusBadge color={COLOR_BLUE} label={crewMember.profession?.name || '-'} />
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('crew.form.company')}</div>
                            <div className="datagrid-content">
                              <StatusBadge color={COLOR_GREEN} label={crewMember.company?.company_name || '-'} />
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('crew.userAccount')}</div>
                            <div className="datagrid-content">
                              <StatusBadge color={crewMember.user ? COLOR_SUCCESS : COLOR_SECONDARY} label={crewMember.user ? t('common.yes') : t('common.no')} />
                              {crewMember.user && (
                                <div className="mt-1 text-muted small">
                                  {t('crew.detailPage.usernameLabel')}: {crewMember.user.username}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                    </PageSection>
                  </Col>

                  <Col md={6}>
                    {/* Current Projects */}
                    {projects.length > 0 && (
                      <PageSection
                        title={t('crew.currentProjects')}
                        description={t('crew.detailPage.currentProjectsDescription')}
                        actions={<StatusBadge color={COLOR_PRIMARY} label={String(projects.length)} />}
                        bodyClassName="p-0"
                      >
                          <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <Table responsive className="table-vcenter app-table mb-0">
                              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                                <tr>
                                  <th>{t('projects.table.name')}</th>
                                  <th>{t('projects.table.status')}</th>
                                  <th>{t('projects.table.startDate')}</th>
                                  <th className="w-1">{t('common.actions')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {projects.map(project => (
                                  <tr key={project.id}>
                                    <td>
                                      <div className="text-truncate" style={{ maxWidth: '100%' }}>
                                        {project.name || ''}
                                      </div>
                                    </td>
                                    <td>
                                      <StatusBadge color={getStatusBadgeColor(project.status)} label={getStatusText(project.status) || '-'} />
                                    </td>
                                    <td>{formatDate(project.start_date)}</td>
                                    <td>
                                      <Button as={Link} to={`/projects/${project.id}`} size="sm" variant="outline-primary">
                                        {t('common.view')}
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                      </PageSection>
                    )}

                    {/* Additional Information Card */}
                    <PageSection
                      title={t('crew.additionalInfo')}
                      description={t('crew.detailPage.additionalInfoDescription')}
                      className={projects.length > 0 ? 'mt-3' : ''}
                    >
                        <div className="datagrid">
                          {crewMember.profession?.description && (
                            <div className="datagrid-item">
                              <div className="datagrid-title">{t('crew.professionDescription')}</div>
                              <div className="datagrid-content">{crewMember.profession.description}</div>
                            </div>
                          )}
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('crew.memberSince')}</div>
                            <div className="datagrid-content">
                              {t('common.notSet')}
                            </div>
                          </div>
                        </div>
                    </PageSection>
                  </Col>
                </Row>
              )}

              {/* Projects Tab */}
              {activeTab === 'projects' && (
                <PageSection
                  title={t('crew.projectHistory')}
                  description={t('crew.detailPage.projectHistoryDescription')}
                  actions={(
                    <Button size="sm" variant="outline-primary" onClick={refetchAllProjects}>
                      <i className="ti ti-refresh me-2" aria-hidden="true" />
                      {t('common.refresh')}
                    </Button>
                  )}
                  bodyClassName="p-0"
                >
                    <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                      <Table responsive className="table table-vcenter app-table mb-0">
                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                          <tr>
                            <th>{t('projects.table.name')}</th>
                            <th>{t('projects.table.startDate')}</th>
                            <th>{t('projects.table.endDate')}</th>
                            <th>{t('projects.table.status')}</th>
                            <th>{t('projects.table.location')}</th>
                            <th className="w-1">{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allProjects.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="text-center text-muted py-4">
                                {t('crew.noProjects')}
                              </td>
                            </tr>
                          ) : (
                            allProjects.map(project => (
                              <tr key={project.id}>
                                <td>
                                  <div className="text-truncate" style={{ maxWidth: '100%' }}>
                                    {project.name || ''}
                                  </div>
                                </td>
                                <td>{formatDate(project.start_date)}</td>
                                <td>{formatDate(project.end_date)}</td>
                                <td>
                                  <StatusBadge color={getStatusBadgeColor(project.status)} label={getStatusText(project.status) || '-'} />
                                </td>
                                <td>
                                  <div className="text-truncate" style={{ maxWidth: '100%' }}>
                                    {project.location || '-'}
                                  </div>
                                </td>
                                <td>
                                  <Button as={Link} to={`/projects/${project.id}`} size="sm" variant="outline-primary" title={t('common.view')}>
                                    <i className="ti ti-eye" aria-hidden="true" />
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                </PageSection>
              )}

              {/* Statistics Tab */}
              {activeTab === 'statistics' && (
                <Row className="g-4">
                  <Col md={6}>
                    <PageSection
                      title={t('crew.projectStatistics')}
                      description={t('crew.detailPage.statisticsDescription')}
                    >
                      <div className="app-stat-grid">
                        <StatsCard title={t('crew.totalProjects')} value={statistics?.total_projects || 0} icon="briefcase" colorClass="primary" />
                        <StatsCard title={t('crew.activeProjects')} value={statistics?.active_projects || 0} icon="bolt" colorClass="success" />
                        <StatsCard title={t('crew.completedProjects')} value={statistics?.completed_projects || 0} icon="circle-check" colorClass="orange" />
                      </div>
                    </PageSection>
                  </Col>

                  <Col md={6}>
                    <PageSection title={t('crew.professionalInfo')}>
                        <div className="datagrid">
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('crew.form.profession')}</div>
                            <div className="datagrid-content">
                              <StatusBadge color={COLOR_BLUE} label={crewMember.profession?.name || '-'} />
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('crew.form.company')}</div>
                            <div className="datagrid-content">
                              <StatusBadge color={COLOR_GREEN} label={crewMember.company?.company_name || '-'} />
                            </div>
                          </div>
                          <div className="datagrid-item">
                            <div className="datagrid-title">{t('crew.userAccount')}</div>
                            <div className="datagrid-content">
                              <StatusBadge color={crewMember.user ? COLOR_SUCCESS : COLOR_SECONDARY} label={crewMember.user ? t('common.yes') : t('common.no')} />
                            </div>
                          </div>
                        </div>
                    </PageSection>
                  </Col>
                </Row>
              )}
        </div>
      </PageSection>
    </PageShell>
  );
};

export default CrewDetail;
